/**
 * Swap domain types and pure helpers.
 *
 * The types mirror the swap aggregator's response shapes one-to-one, reached
 * through the gateway under /v1/swap. The gateway is a pass-through for these
 * records, so the shapes here are the aggregator's contract: quotes carry a
 * uniform fee structure split into "already deducted from the output" and
 * "paid out-of-pocket" buckets, and a monetization echo the UI must disclose.
 *
 * The helpers are pure so they can be unit tested without the network: decimal
 * string to smallest-units conversion (BigInt, never floats, so an 18-decimal
 * amount cannot lose precision), the reverse formatting, and the merge of
 * per-provider chain lists into one deduplicated picker list.
 */

/** One chain as reported by the aggregator, normalized to standard ids. */
export interface SwapChain {
  id: number;
  name: string;
  displayName?: string;
  icon?: string;
  enabled: boolean;
}

/** One token on a chain, with the providers that can route it. */
export interface SwapToken {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  icon?: string;
  providers: string[];
}

/** A single fee component of a quote. */
export interface SwapFeeDetail {
  amount: string;
  formatted?: string;
  token: string;
  usd?: string;
}

/**
 * Uniform fee structure. service, destinationGas, and platform are already
 * deducted from amountOut (display only, never subtract again); originGas is
 * the out-of-pocket cost paid separately by the wallet, and outOfPocketUsd is
 * its USD sum, the only figure that adds to the user's true cost.
 */
export interface SwapQuoteFees {
  service?: SwapFeeDetail;
  destinationGas?: SwapFeeDetail;
  platform?: SwapFeeDetail;
  originGas?: SwapFeeDetail;
  totalUsd?: string;
  outOfPocketUsd?: string;
}

/** The platform-fee disclosure echoed on a quote when monetization is active. */
export interface SwapMonetization {
  mode: string;
  bps?: number;
  recipient?: string;
  feeUsd?: string;
}

/** One step of the execute flow, for "Step 1 of N" affordances. */
export interface SwapStepIntent {
  kind: string;
  description: string;
  chainId: number;
}

/**
 * The origin-side transaction a quote will execute: the router to call, the swap
 * calldata, the native value to send, and the provider's gas suggestion. This
 * mirrors the swap service's TransactionInfo one-to-one. For a native-in EVM
 * swap this is everything needed to build the unsigned EIP-1559 envelope except
 * the sender nonce, which the browser reads separately (see getSwapNonce). The
 * account-abstraction fields are optional because a provider may omit its gas
 * suggestion, in which case the caller supplies a floor.
 */
export interface SwapTransactionInfo {
  /** The DEX/bridge router to call (the transaction `to`), 0x address. */
  to: string;
  /** The swap calldata (the transaction `data`), 0x hex; may be "0x" or empty. */
  data: string;
  /** The native value to send, in Wei, as a base-10 integer string. */
  value: string;
  /** Max fee per gas in Wei, base-10 string; absent when the provider omits it. */
  maxFeePerGas?: string;
  /** Max priority fee per gas in Wei, base-10 string; absent when omitted. */
  maxPriorityFeePerGas?: string;
  /** Gas limit as a base-10 string; absent when the provider omits it. */
  gasLimit?: string;
  /** The origin EVM chain id this transaction executes on. */
  chainId: number;
}

/**
 * The ERC20 approve step a swap needs before the swap tx itself, when the origin
 * currency is an ERC20 token rather than the native coin. A native-in swap
 * leaves this absent. This build is native-in only, so its presence is the
 * signal to block execution with a not-yet-supported message rather than sign a
 * swap that would revert for lack of allowance.
 */
export interface SwapApprovalStep {
  /** The token contract to approve on, 0x address. */
  to: string;
  /** The approve(spender, amount) calldata, 0x hex. */
  data: string;
  /** The origin chain id of the approval. */
  chainId: number;
}

/** One provider's quote inside the aggregated response. */
export interface SwapQuote {
  provider: string;
  originChainId?: number;
  destinationChainId?: number;
  originCurrency?: string;
  destinationCurrency?: string;
  amountIn?: string;
  amountInUsd?: string;
  amountOut?: string;
  amountOutUsd?: string;
  amountOutMin?: string;
  priceImpactPercent?: string;
  estimatedTime?: number;
  expiresAt?: number;
  fees?: SwapQuoteFees;
  monetization?: SwapMonetization;
  requiresApproval?: boolean;
  estimatedSteps?: SwapStepIntent[];
  preflightWarnings?: string[];
  requestId?: string;
  /**
   * The origin-side transaction to execute, present on a successful quote that
   * carries an executable transaction. Absent on a failed quote or one that
   * returns amounts only.
   */
  transaction?: SwapTransactionInfo;
  /**
   * The ERC20 approve step required before the swap, present only for an ERC20
   * origin. Native-in swaps leave this absent; its presence blocks execution in
   * this native-in-only build.
   */
  approval?: SwapApprovalStep;
  /** Set when this provider failed; the quote carries no amounts then. */
  error?: string;
}

/** The aggregated fan-out response: every provider's quote plus the winner. */
export interface AggregatedSwapQuotes {
  quotes: SwapQuote[];
  /** Provider name with the best net value, or absent when none succeeded. */
  best?: string;
}

/** The request body for a quote fan-out, mirroring the aggregator contract. */
export interface SwapQuoteRequestBody {
  user: string;
  originChainId: number;
  destinationChainId: number;
  originCurrency: string;
  destinationCurrency: string;
  /** Base-10 integer string in smallest token units. */
  amount: string;
  recipient?: string;
  slippage?: number;
  /**
   * Which side `amount` pins. "EXACT_INPUT" (default): amount is the origin
   * amount, output is computed. "EXACT_OUTPUT": amount is the desired
   * destination amount, required input is computed.
   */
  tradeType?: 'EXACT_INPUT' | 'EXACT_OUTPUT';
}

/** One tracked swap as served by the swap tracker's /swaps endpoint. */
export interface TrackedSwap {
  id: number;
  provider: string;
  requestId: string;
  userAddress?: string;
  recipient?: string;
  originChainId: number;
  destinationChainId: number;
  originCurrency?: string;
  destinationCurrency?: string;
  /** Display facts snapshotted at execute time; -1 decimals = not supplied. */
  originSymbol?: string;
  originDecimals?: number;
  destinationSymbol?: string;
  destinationDecimals?: number;
  amountIn?: string;
  amountOut?: string;
  amountInUsd?: string;
  amountOutUsd?: string;
  feeUsd?: string;
  realizedFeeUsd?: string;
  /** Realized fee in token form (Across pays in the output token). Amount is
   *  raw units of realizedFeeToken; both empty for Relay's USD ledger. */
  realizedFeeAmount?: string;
  realizedFeeToken?: string;
  status: string;
  srcTxHash?: string;
  dstTxHash?: string;
  createdAt: string;
  updatedAt: string;
}

/** Chain-id to display-name map for the chains the swap flow touches. */
const CHAIN_NAMES: Record<number, string> = {
  1: 'Ethereum',
  10: 'Optimism',
  56: 'BNB Chain',
  137: 'Polygon',
  8453: 'Base',
  42161: 'Arbitrum',
  11155111: 'Sepolia',
  84532: 'Base Sepolia',
  421614: 'Arbitrum Sepolia',
};

/**
 * Human-readable chain name for a standard chain id, falling back to the id
 * itself so an unmapped chain stays identifiable rather than blank.
 */
export function chainNameFor(chainId: number): string {
  return CHAIN_NAMES[chainId] ?? `Chain ${chainId}`;
}

/** The pseudo-addresses providers use for a chain's native coin. */
export function isNativeCurrency(address: string | undefined): boolean {
  if (!address) return false;
  const a = address.toLowerCase();
  return a === '0x0000000000000000000000000000000000000000' ||
    a === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
}

/**
 * Convert a user-typed decimal amount to a base-10 integer string in smallest
 * token units. BigInt arithmetic throughout so an 18-decimal token cannot hit
 * float precision loss.
 *
 * @param amount The typed amount, for example "1.5" or ".25".
 * @param decimals The token's decimal places.
 * @returns The smallest-units string, or null when the input is not a valid
 *   positive decimal number or carries more fractional digits than the token
 *   supports.
 */
export function toSmallestUnits(amount: string, decimals: number): string | null {
  const trimmed = amount.trim();
  if (!/^\d*\.?\d*$/.test(trimmed)) return null;
  const [wholeRaw = '', fracRaw = ''] = trimmed.split('.');
  if (wholeRaw === '' && fracRaw === '') return null;
  if (fracRaw.length > decimals) return null;
  const whole = wholeRaw === '' ? '0' : wholeRaw;
  const frac = fracRaw.padEnd(decimals, '0');
  try {
    const units = BigInt(whole) * 10n ** BigInt(decimals) + BigInt(frac === '' ? '0' : frac);
    if (units <= 0n) return null;
    return units.toString();
  } catch {
    return null;
  }
}

/**
 * Convert a base-units integer string to its exact decimal-string equivalent,
 * losslessly and with no rounding. This is the precise inverse of the gateway's
 * base-unit reconstruction: an integer part, a dot, and up to `decimals`
 * fractional digits with trailing zeros trimmed, and never more than `decimals`
 * fractional digits. Unlike fromSmallestUnits and formatTokenAmount, which round
 * or cap fractional digits for display, this keeps every significant digit so
 * the result can be fed back to a base-unit reconstruction and recover the exact
 * input.
 *
 * The swap record's `amount` field must be a human decimal because the gateway's
 * pre-sign reconciliation reads it as one and multiplies by 10^decimals before
 * comparing to the signed payload value. Handing it a base-units string there
 * would multiply by 10^decimals a second time and never match. So this is used
 * to turn a quote's base-units input back into the human decimal the record
 * expects.
 *
 * @param wei Base-10 integer string in smallest units (no sign, no 0x, no
 *   scientific notation).
 * @param decimals The asset's decimal places.
 * @returns The exact decimal string, or null when `wei` is not a non-negative
 *   base-10 integer.
 */
export function weiToDecimalString(wei: string, decimals: number): string | null {
  const trimmed = wei.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  let units: bigint;
  try {
    units = BigInt(trimmed);
  } catch {
    return null;
  }
  if (decimals <= 0) return units.toString();
  const base = 10n ** BigInt(decimals);
  const whole = units / base;
  const frac = units % base;
  if (frac === 0n) return whole.toString();
  // Left-pad the fractional part to the full width so leading zeros survive
  // (1 wei at 18 decimals is 17 zeros then a 1), then trim only the trailing
  // zeros so the string stays as short as it can be without losing precision.
  const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole}.${fracStr}`;
}

/**
 * Format a smallest-units integer string as a human-readable decimal.
 *
 * @param raw Base-10 integer string in smallest units.
 * @param decimals The token's decimal places.
 * @param maxFraction Maximum fractional digits to keep, trailing zeros trimmed.
 * @returns The formatted amount, or "" when raw is not a valid integer.
 */
export function fromSmallestUnits(raw: string, decimals: number, maxFraction = 6): string {
  if (!/^\d+$/.test(raw)) return '';
  let units: bigint;
  try {
    units = BigInt(raw);
  } catch {
    return '';
  }
  const base = 10n ** BigInt(decimals);
  const whole = units / base;
  const frac = units % base;
  if (frac === 0n) return whole.toString();
  const fracStr = frac.toString().padStart(decimals, '0').slice(0, maxFraction).replace(/0+$/, '');
  return fracStr === '' ? whole.toString() : `${whole}.${fracStr}`;
}

/**
 * Merge the per-provider chain lists from /v1/swap/chains into one
 * deduplicated list for the chain picker, sorted by name ascending. Ids are
 * already normalized to the standard ids by the aggregator, so equal ids from
 * two providers are the same chain; the first record wins and later ones only
 * fill a missing icon.
 *
 * @param byProvider Map of provider name to that provider's chains.
 * @returns One deduplicated, name-sorted chain list.
 */
export function mergeChains(byProvider: Record<string, SwapChain[]>): SwapChain[] {
  const merged = new Map<number, SwapChain>();
  for (const chains of Object.values(byProvider)) {
    for (const chain of chains) {
      const existing = merged.get(chain.id);
      if (!existing) {
        merged.set(chain.id, { ...chain });
      } else if (!existing.icon && chain.icon) {
        existing.icon = chain.icon;
      }
    }
  }
  return [...merged.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Whether a chain should appear in the picker. The aggregator passes through
 * chains it has no canonical mapping for with a placeholder "Chain <id>" name,
 * and providers list testnets alongside mainnets; neither belongs in a wallet
 * chain picker, so both are dropped. Dropping by name (not an allowlist) keeps
 * newly shipped mainnet chains visible without a frontend change.
 *
 * @param chain The chain to test.
 * @returns True when the chain is a named mainnet chain.
 */
export function isDisplayableChain(chain: SwapChain): boolean {
  const name = (chain.displayName || chain.name || '').trim();
  if (name === '') return false;
  if (/^chain[\s_-]?\d+$/i.test(name)) return false;
  return chain.enabled;
}

/**
 * Whether a chain's name marks it as a test network. The marker list covers
 * the test-network naming families providers actually use: generic words
 * (testnet, devnet), Ethereum's named testnets (Sepolia, Holesky, Goerli),
 * and per-chain brands (Polygon Amoy/Mumbai, Avalanche Fuji, BNB Chapel,
 * Celo Alfajores). A miss here misclassifies a testnet as mainnet, which in
 * curateChains hides every correctly detected testnet — so prefer adding a
 * marker over relying on the generic words alone.
 */
export function isTestnetChain(chain: SwapChain): boolean {
  return /testnet|devnet|sepolia|holesky|goerli|amoy|mumbai|fuji|chapel|alfajores|cardona/i.test(
    chain.displayName || chain.name || '',
  );
}

/**
 * Curate the merged chain list for display: drop unmapped placeholder and
 * disabled entries always, and drop testnets only when mainnet chains are
 * also present. Against a mainnet aggregator this hides stray testnets; when
 * the aggregator itself is pointed at testnets (every chain is a testnet),
 * the list is served as-is so the picker is never emptied by the filter.
 *
 * @param chains The merged chain list.
 * @returns The chains fit for the picker, order preserved.
 */
export function curateChains(chains: SwapChain[]): SwapChain[] {
  const displayable = chains.filter(isDisplayableChain);
  const mainnets = displayable.filter((c) => !isTestnetChain(c));
  return mainnets.length > 0 ? mainnets : displayable;
}

/**
 * Format a USD string from the aggregator for display. The upstream values are
 * plain decimal strings; anything unparsable or non-positive renders as "" so
 * the caller can fall back to an em dash rather than showing "$0.00" for
 * "unknown".
 *
 * @param usd The raw USD string, for example "0.029997".
 * @returns "$X.XX" (two decimals, or four when under a cent), or "".
 */
export function formatUsd(usd: string | undefined): string {
  if (!usd) return '';
  const value = Number(usd);
  if (!Number.isFinite(value) || value <= 0) return '';
  return value < 0.01 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`;
}

/**
 * Compact display form of a token amount for list rows, following the app's
 * decimal conventions: millions/billions get two decimals with a suffix
 * (12.00M), values from 1 up get two decimals (10.90), and sub-1 values keep
 * up to six significant fractional digits so a dust amount stays non-zero.
 *
 * @param value Decimal string, e.g. the output of fromSmallestUnits.
 * @returns The compact label, or "" for a missing or unparsable value.
 */
export function formatTokenAmount(value: string): string {
  if (!value) return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1) return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n === 0) return '0';
  // Sub-1: keep six significant fractional digits, trimmed of trailing zeros.
  const fixed = n.toFixed(8);
  const trimmed = fixed.replace(/0+$/, '').replace(/\.$/, '');
  return trimmed === '0' ? value : trimmed;
}

/**
 * Format a duration in seconds as a short human label, for example "30s" or
 * "2m 10s".
 *
 * @param seconds Duration in seconds.
 * @returns The label, or "" for a missing or non-positive duration.
 */
export function formatDuration(seconds: number | undefined): string {
  if (!seconds || seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
}

/**
 * Order the quotes of a fan-out for display: the best quote first, then the
 * other successful quotes, then failed providers. Within each group the
 * original order is kept, so the sort is stable and cheap.
 *
 * @param response The aggregated response.
 * @returns A new ordered array; the input is not mutated.
 */
export function orderQuotes(response: AggregatedSwapQuotes): SwapQuote[] {
  const rank = (q: SwapQuote): number => {
    if (q.error) return 2;
    return q.provider === response.best ? 0 : 1;
  };
  return [...response.quotes].sort((a, b) => rank(a) - rank(b));
}
